import React, { memo, useRef, useCallback, useMemo } from 'react';
import { classes } from '@ardoq/common-helpers';
import { Y_AXIS_MARGIN_TIMELINE } from '../consts';
import {
  ItemTops,
  TimelineLabelWithRenderingParameters,
  TimelineViewCommands,
} from '../../types';
import { LabelsColumnListLink } from './LabelsColumnListLink';
import { ColorIndicator } from './ColorIndicator';
import {
  Label,
  LabelAndSubLabelContainer,
  LabelAndLinkContainer,
  TimelineGroupExpander,
} from './atoms';
import { GroupSubLabel } from './GroupSubLabel';
import { ComponentIcon } from './ComponentIcon';
import { ItemLabelStylesWrapper } from './ItemLabelStylesWrapper';
import { ModernizedLabelLayout } from './ModernizedLabelLayout';
import { POPOVER_ELEMENT_TYPE } from '../../consts';
import { getRowHeightFromItemTops } from '../../calculations';

type RowDimensions = {
  renderedItemTops: ItemTops;
  rowIndex: number;
  containerWidth: number;
};

type ItemLabelProps = {
  itemLabelData: TimelineLabelWithRenderingParameters;
  contextMenuDataAttribute: string;
  isLastItem: boolean;
  hasGrouping: boolean;
  viewInstanceId: string;
  transitLinksById: Map<string, number[]>;
  rowHeight: number;
  commands: TimelineViewCommands;
};

export const ItemLabel = memo(
  ({
    itemLabelData,
    hasGrouping,
    isLastItem,
    contextMenuDataAttribute,
    viewInstanceId,
    transitLinksById,
    renderedItemTops,
    rowIndex,
    containerWidth,
    commands,
  }: Omit<ItemLabelProps, 'rowHeight'> & RowDimensions) => {
    const labelAndLinkContainerWidth =
      containerWidth - Y_AXIS_MARGIN_TIMELINE * 2;

    const { rowHeight, rowTop } = useMemo(
      () => ({
        rowHeight: getRowHeightFromItemTops(renderedItemTops, rowIndex),
        rowTop: renderedItemTops[rowIndex],
      }),
      [renderedItemTops, rowIndex]
    );

    return (
      <LabelAndLinkContainer
        style={{
          ...itemLabelData.groupSeparatorStyle,
          height: rowHeight,
          width: labelAndLinkContainerWidth,
          top: `${rowTop}px`,
        }}
      >
        <ItemLabelContents
          itemLabelData={itemLabelData}
          hasGrouping={hasGrouping}
          isLastItem={isLastItem}
          contextMenuDataAttribute={contextMenuDataAttribute}
          viewInstanceId={viewInstanceId}
          transitLinksById={transitLinksById}
          rowHeight={rowHeight}
          commands={commands}
        />
      </LabelAndLinkContainer>
    );
  }
);

const ItemLabelContents = memo(
  ({
    itemLabelData,
    hasGrouping,
    isLastItem,
    contextMenuDataAttribute,
    viewInstanceId,
    transitLinksById,
    rowHeight,
    commands,
  }: ItemLabelProps) => {
    const { setHoveredItem, setFocusedItem } = commands;
    const {
      itemModel: {
        id: itemId,
        rawItemIdPath,
        modelId,
        itemClass,
        isGroup,
        excluded,
        treeDepth,
        itemLabels,
      },
      componentRepresentationData,
      contrastedComponentColor,
      isComponentHierarchyCollapsed,
      isFirstChild,
      colorIndicatorColor,
      isLastChild,
    } = itemLabelData;
    const isDescendant = treeDepth > 0;
    const { mainLabel, subLabel } = itemLabels;

    const toggleCollapseExpandGroup = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (!rawItemIdPath) return;
        e.stopPropagation();
        commands.collapseExpandGroup(
          rawItemIdPath,
          !isComponentHierarchyCollapsed,
          viewInstanceId
        );
      },
      [rawItemIdPath, isComponentHierarchyCollapsed, viewInstanceId, commands]
    );

    const labelContainerProps = {
      onMouseEnter: () => setHoveredItem(itemId, viewInstanceId),
      onMouseLeave: isLastItem
        ? () => setHoveredItem(null, viewInstanceId)
        : undefined,
      onClick: () => setFocusedItem(modelId, viewInstanceId),
      ...(contextMenuDataAttribute
        ? { [contextMenuDataAttribute]: modelId }
        : {}),
    };

    const sublabelContainer = useRef<HTMLDivElement>(null);

    const labelsColumnListLinkProps = {
      treeDepth,
      hasGrouping,
      isGroup,
      rowHeight,
      transitLinks: transitLinksById.get(itemId) ?? [],
      isFirstChild,
      isLastChild,
      isComponentHierarchyCollapsed,
    };

    if (!itemLabels.legacyLabelParts) {
      return (
        <ModernizedLabelLayout
          itemLabelData={itemLabelData}
          labelsColumnListLinkProps={labelsColumnListLinkProps}
          hasGrouping={hasGrouping}
          labelContainerProps={labelContainerProps}
          toggleCollapseExpandGroup={toggleCollapseExpandGroup}
          viewInstanceId={viewInstanceId}
          commands={commands}
        />
      );
    }

    return (
      <>
        {isDescendant && (
          <LabelsColumnListLink {...labelsColumnListLinkProps} />
        )}
        <ItemLabelStylesWrapper
          isGroupLabel={hasGrouping && isGroup}
          excluded={excluded}
          labelContainerProps={labelContainerProps}
        >
          <ColorIndicator
            isGroup={hasGrouping && isGroup}
            color={colorIndicatorColor}
          />

          {isGroup && rawItemIdPath && (
            <TimelineGroupExpander
              deprecated_onClick={toggleCollapseExpandGroup}
              className="expander"
              direction={isComponentHierarchyCollapsed ? 'down' : 'up'}
            />
          )}
          {componentRepresentationData && (
            <ComponentIcon
              color={contrastedComponentColor}
              itemClass={itemClass}
              componentRepresentationData={componentRepresentationData}
            />
          )}
          <LabelAndSubLabelContainer
            data-graph-node-id={itemId}
            data-popover-element-type={POPOVER_ELEMENT_TYPE.MAIN_LABEL}
          >
            <Label className={classes('component-name', 'component')}>
              {mainLabel}
            </Label>
            {subLabel ? (
              <GroupSubLabel ref={sublabelContainer} groupSublabel={subLabel} />
            ) : null}
          </LabelAndSubLabelContainer>
        </ItemLabelStylesWrapper>
      </>
    );
  }
);
